import type { EstimatorBand } from '../api'

/**
 * The estimator's geometry and its ink.
 *
 * ## What this file is allowed to compute, and what it is not
 *
 * The Polling Studio's standing rule is that nothing in the client computes a percentage: every
 * share, margin, design effect, rate and probability arrives from the server with its base and
 * its assumptions attached, which is what guarantees a figure and the table beside it agree.
 *
 * This file does not break that rule and must not be edited into breaking it. What it does is
 * DRAW a statistic the server already sent. {@link density} evaluates a normal curve at a pixel
 * column from a served `(estimate, standardError)` pair — the same act as choosing the height
 * of a bar from a served percentage, performed 300 times across a lane instead of once. It
 * derives no new quantity: the mean, the spread, the interval and the lead probability are all
 * the server's, and if any of them is missing the lane is drawn as absent rather than as
 * anything this file worked out for itself.
 */

/**
 * The normal density, unnormalised.
 *
 * The constant `1/(σ√2π)` is deliberately omitted: every lane is scaled to its own peak by
 * {@link laneScale}, so a leading coefficient would cancel out. Leaving it in would suggest the
 * curve's absolute height means something, and it does not — only its shape and its area do.
 */
export function density(x: number, mean: number, sd: number): number {
  if (!(sd > 0)) return 0
  const z = (x - mean) / sd
  return Math.exp(-0.5 * z * z)
}

/**
 * Relative lane height, so AREA is comparable across lanes.
 *
 * A density integrates to one. Drawing every lane to the same pixel height would break that:
 * a precise estimate and a vague one would look equally confident, which is the exact
 * misreading a bar chart of vote shares already produces and this figure exists to correct.
 *
 * So height goes as 1/σ, normalised against the SHARPEST lane on the board. A tightly measured
 * share is a tall narrow spike; a thinly measured one is a low broad hill; and the ink under
 * each is the same. That is the whole grammar of the figure, in one ratio.
 */
export function laneScale(sd: number | null, sharpest: number): number {
  if (!sd || sd <= 0 || sharpest <= 0) return 0
  return Math.max(0.18, Math.min(1, sharpest / sd))
}

/** The smallest standard error on the board — the reference the other lanes are drawn against. */
export function sharpestSd(bands: EstimatorBand[]): number {
  let best = Number.POSITIVE_INFINITY
  for (const band of bands) {
    if (band.standardError && band.standardError > 0 && band.standardError < best) {
      best = band.standardError
    }
  }
  return Number.isFinite(best) ? best : 0
}

export interface ShareDomain { from: number, to: number }

/**
 * The share axis.
 *
 * ## It does not start at zero, and that is defensible here
 *
 * The zero-baseline rule exists because a bar's LENGTH encodes its value, and truncating the
 * axis lies about the ratio between two bars. Nothing on this figure encodes a value as a
 * length from an origin: a lane's position is its mean and its width is its uncertainty, both
 * of which are read against the axis's own ticks. Padding the domain to the data plus three
 * standard errors is what makes the curves legible at all — over a 0–100% axis, a ±3 point
 * interval is four pixels wide and the figure has nothing to say.
 */
export function shareDomain(bands: EstimatorBand[]): ShareDomain {
  let lo = Number.POSITIVE_INFINITY
  let hi = Number.NEGATIVE_INFINITY
  for (const band of bands) {
    if (band.estimate === null) continue
    const sd = band.standardError ?? 0
    lo = Math.min(lo, band.estimate - sd * 3.6)
    hi = Math.max(hi, band.estimate + sd * 3.6)
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { from: 0, to: 1 }
  const pad = Math.max(0.012, (hi - lo) * 0.06)
  return { from: Math.max(0, lo - pad), to: Math.min(1, hi + pad) }
}

/** Axis ticks on round share values, chosen for the span rather than for a fixed count. */
export function shareTicks(domain: ShareDomain): number[] {
  const span = domain.to - domain.from
  const step = span > 0.5 ? 0.1 : span > 0.25 ? 0.05 : span > 0.1 ? 0.02 : 0.01
  const out: number[] = []
  for (let v = Math.ceil(domain.from / step) * step; v <= domain.to + 1e-9; v += step) {
    out.push(Math.round(v / step) * step)
  }
  return out
}

// ── rendering numbers ────────────────────────────────────────────────────────

/**
 * A share, as a percentage to one decimal.
 *
 * Null renders as an em dash and NEVER as zero. That single rule is the last line of defence
 * in this feature: a `?? 0` here states that nobody supports a candidate at the moment the
 * server said it could not tell.
 */
export const pct = (value: number | null | undefined, dp = 1) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(dp)}%`

/** A margin or a gap, in percentage POINTS — the unit a difference is actually quoted in. */
export const pp = (value: number | null | undefined, dp = 1) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(dp)} pp`

export const signedPp = (value: number | null | undefined, dp = 1) =>
  value === null || value === undefined
    ? '—'
    : `${value >= 0 ? '+' : '−'}${(Math.abs(value) * 100).toFixed(dp)} pp`

export const count = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : Math.round(value).toLocaleString()

/**
 * A probability, worded so it cannot be read as a share of anything.
 *
 * Capped in language rather than in value: a served 1.0 is rounded, not certain, and printing
 * "100%" beside a lead of thirteen points would be the most overconfident sentence on a board
 * built to prevent overconfidence.
 */
export function probability(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (value >= 0.9995) return '> 99.9%'
  if (value <= 0.0005) return '< 0.1%'
  return `${(value * 100).toFixed(1)}%`
}
